import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { DANCE_FORMS, DANCE_LEVELS } from "../lib/dance";
import FormLabel from "./FormLabel";
import FormFieldError from "./FormFieldError";
import { ReadableNavButton } from "./Sticker";
import { ReadablePillToggle } from "./createEvent/CreateEventFormUi";

/**
 * Multi-select dance forms (+ optional custom dance and per-dance RSVP levels).
 * Value shape: [{ name: string, level: string }]
 */
export default function DanceFormsField({
  value = [],
  onChange,
  error,
  errorId,
  id,
  required = true,
  showLevelsSection = false,
  testIdPrefix = "profile-dance",
}) {
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customLevel, setCustomLevel] = useState("Beginner");
  const [showLevels, setShowLevels] = useState(false);

  useEffect(() => {
    if (value.some((d) => d.level && d.level !== "Beginner")) {
      setShowLevels(true);
    }
  }, [value]);

  const customNames = value
    .map((d) => d.name)
    .filter((name) => !DANCE_FORMS.includes(name));

  function emit(next) {
    onChange(next);
  }

  function toggle(name) {
    const exists = value.find((d) => d.name === name);
    if (exists) {
      emit(value.filter((d) => d.name !== name));
      return;
    }
    emit([...value, { name, level: "Beginner" }]);
  }

  function setLevel(name, level) {
    emit(value.map((d) => (d.name === name ? { ...d, level } : d)));
  }

  function addCustom() {
    const name = customName.trim();
    if (!name) return;
    if (value.some((d) => d.name.toLowerCase() === name.toLowerCase())) {
      toast.error("You already have that dance.");
      return;
    }
    emit([...value, { name, level: customLevel }]);
    setCustomName("");
    setCustomLevel("Beginner");
    setShowCustom(false);
  }

  function cancelCustom() {
    setShowCustom(false);
    setCustomName("");
    setCustomLevel("Beginner");
  }

  return (
    <div id={id} className="space-y-4">
      <div>
        <FormLabel className="block mb-2">
          Dances you do{required ? " *" : ""}
        </FormLabel>
        <div className="flex gap-2 flex-wrap">
          {DANCE_FORMS.map((d) => {
            const active = value.some((f) => f.name === d);
            return (
              <ReadablePillToggle
                key={d}
                active={active}
                onClick={() => toggle(d)}
                data-testid={`${testIdPrefix}-${d.toLowerCase()}-btn`}
              >
                {d}
              </ReadablePillToggle>
            );
          })}
          {customNames.map((d) => (
            <ReadablePillToggle
              key={d}
              active
              onClick={() => toggle(d)}
              data-testid={`${testIdPrefix}-custom-${d.toLowerCase().replace(/\s+/g, "-")}-btn`}
            >
              {d}
            </ReadablePillToggle>
          ))}
        </div>

        {showCustom ? (
          <div className="mt-3 space-y-4 rounded-md2 border border-black/[0.08] bg-cream/50 p-4">
            <label className="block">
              <FormLabel className="block mb-2">Dance name</FormLabel>
              <input
                className="field"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g. Cha-cha, West Coast Swing"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustom();
                  }
                  if (e.key === "Escape") cancelCustom();
                }}
                data-testid="custom-dance-input"
              />
            </label>
            <div>
              <FormLabel className="block mb-2">Level</FormLabel>
              <div className="flex gap-2 flex-wrap">
                {DANCE_LEVELS.map((lv) => (
                  <ReadablePillToggle
                    key={lv}
                    active={customLevel === lv}
                    onClick={() => setCustomLevel(lv)}
                    data-testid={`custom-dance-level-${lv.toLowerCase()}-btn`}
                  >
                    {lv}
                  </ReadablePillToggle>
                ))}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <ReadableNavButton
                type="button"
                variant="primary"
                onClick={addCustom}
                disabled={!customName.trim()}
                data-testid="confirm-custom-dance-btn"
              >
                Add dance
              </ReadableNavButton>
              <button
                type="button"
                onClick={cancelCustom}
                className="font-ui font-semibold text-[13px] text-ink/70 underline min-h-[44px] px-2"
                data-testid="cancel-custom-dance-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <ReadableNavButton
            type="button"
            onClick={() => {
              setCustomLevel("Beginner");
              setShowCustom(true);
            }}
            className="mt-3"
            data-testid="add-custom-dance-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={2} aria-hidden /> Add your own
          </ReadableNavButton>
        )}

        {error ? <FormFieldError id={errorId}>{error}</FormFieldError> : null}
      </div>

      {showLevelsSection && value.length > 0 ? (
        showLevels ? (
          value.map((df) => (
            <div key={df.name}>
              <FormLabel className="block mb-2">{df.name} level on RSVPs</FormLabel>
              <div className="flex gap-2 flex-wrap">
                {DANCE_LEVELS.map((lv) => (
                  <ReadablePillToggle
                    key={lv}
                    active={df.level === lv}
                    onClick={() => setLevel(df.name, lv)}
                    data-testid={`profile-level-${df.name.toLowerCase()}-${lv.toLowerCase()}-btn`}
                  >
                    {lv}
                  </ReadablePillToggle>
                ))}
              </div>
            </div>
          ))
        ) : (
          <ReadableNavButton
            type="button"
            onClick={() => setShowLevels(true)}
            className="mt-1"
            data-testid="profile-dance-levels-toggle"
          >
            Set RSVP levels (optional)
          </ReadableNavButton>
        )
      ) : null}
    </div>
  );
}
